import { Settings, AreaChart } from '@mui/icons-material';
import React from 'react';
import { Link } from 'react-router-dom';


interface OverviewPlaceholderProps {
  onGenerateSummary: () => void;
  isGenerating: boolean;
  isGenerationPossible: boolean;
}

const OverviewPlaceholder: React.FC<OverviewPlaceholderProps> = ({ 
  onGenerateSummary, 
  isGenerating,
  isGenerationPossible 
}) => {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '60vh',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '3rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        maxWidth: '600px',
        width: '100%'
      }}>
        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          background: isGenerationPossible 
            ? 'linear-gradient(90deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)'
            : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem',
          fontSize: '2rem'
        }}>
          {isGenerationPossible ? (
            <AreaChart style={{ color: 'white', fontSize: '3rem' }} />
          ) : (
            <Settings style={{ color: 'white', fontSize: '3rem' }} />
          )}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '600',
          color: '#1a202c',
          marginBottom: '1rem',
          lineHeight: '1.3'
        }}>
          {isGenerationPossible ? 'Let\'s create some insights!' : 'Configuration Required'}
        </h2>

        {/* Description */}
        <p style={{
          fontSize: '1.1rem',
          color: '#4a5568',
          lineHeight: '1.6',
          marginBottom: '2rem'
        }}>
          {isGenerationPossible 
            ? 'Your executive summary hasn\'t been generated yet. Click the button below to create a comprehensive overview that combines insights from your development and support data.'
            : 'We don\'t have enough data to generate an executive summary. Please ensure you have at least one configured integration and that it has finished syncing for the first time.'
          }
        </p>

        {isGenerationPossible ? (
          <>
            {/* Features list */}
            <div style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '1rem'
              }}>
                Your summary will include:
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '0.95rem',
                color: '#4a5568'
              }}>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#48bb78', marginRight: '0.5rem' }}>✓</span>
                  Key pain points and operational challenges
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#48bb78', marginRight: '0.5rem' }}>✓</span>
                  Notable successes and positive trends
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#48bb78', marginRight: '0.5rem' }}>✓</span>
                  Immediate priorities for action
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#48bb78', marginRight: '0.5rem' }}>✓</span>
                  Performance insights for staff across your teams
                </li>
              </ul>
            </div>

            {/* Generate button */}
            <button
              onClick={onGenerateSummary}
              disabled={isGenerating}
              style={{
                background: isGenerating 
                  ? '#a0aec0' 
                  : 'linear-gradient(90deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '1rem 2rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isGenerating ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)',
                transform: isGenerating ? 'none' : 'translateY(0)',
                minWidth: '200px'
              }}
              onMouseOver={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
            >
              {isGenerating ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '0.5rem'
                  }} />
                  Generating Summary...
                </span>
              ) : (
                'Generate Executive Summary'
              )}
            </button>

            {/* Loading message */}
            {isGenerating && (
              <p style={{
                fontSize: '0.9rem',
                color: '#718096',
                marginTop: '1rem',
                fontStyle: 'italic'
              }}>
                This may take a few moments as we analyze your data...
              </p>
            )}
          </>
        ) : (
          <>
            {/* Configuration steps */}
            <div style={{
              background: 'rgb(247, 250, 252)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '1rem'
              }}>
                To get started:
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '0.95rem',
                color: '#2d3748'
              }}>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2d3748', marginRight: '0.5rem' }}>1.</span>
                  Configure at least one integration
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2d3748', marginRight: '0.5rem' }}>2.</span>
                  Wait for the initial data sync to complete
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2d3748', marginRight: '0.5rem' }}>3.</span>
                  Return here to generate your executive summary
                </li>
              </ul>
            </div>

            {/* Settings link */}
            <Link
              to="/settings"
              style={{
                background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '1rem 2rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(245, 101, 101, 0.3)',
                minWidth: '200px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 101, 101, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 101, 101, 0.3)';
              }}
            >
              Go to Settings
            </Link>
          </>
        )}
      </div>

      {/* Add CSS animation for spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OverviewPlaceholder;
